import React, { useMemo, useState } from 'react'
import { Modal, Button } from 'react-bootstrap'
import { apiResource } from '../../utils'
import { doRequestPost } from '../../api'
import { doToast } from '../../utils'
import { toast } from 'react-toastify'
import { useSaveUpdate } from '../../hooks/process-data'
import LoadingIcon from '../../components/loading/LoadingIcon'

export default function CartProductosModal({ show, onHide, items, setReload }) {
  const [loadingAction, setLoadingAction] = useState(null) // { idProductSku, action: 'minus' | 'plus' | 'remove' }

  const totals = useMemo(() => {
    const subtotal = (items || []).reduce((sum, it) => sum + (it.subtotal || 0), 0)
    // const cargoPorOrden = parseFloat(((subtotal * 0.04) * 1.16) + 5)
    const cargoPorOrden = 0
    const total = subtotal + cargoPorOrden
    return {
      subtotal: subtotal.toFixed(2),
      cargo_por_orden: cargoPorOrden.toFixed(2),
      total: total.toFixed(2)
    }
  }, [items])

  const { doSubmit, setDoSubmit } = useSaveUpdate({
    data: useMemo(() => ({
      p_cargo_por_orden: totals.cargo_por_orden
    }), [totals.cargo_por_orden]),
    url: '/product/mercadopago/pay',
    setReload: (data) => {
      if (data && data.link_pay) window.location.href = data.link_pay
    }
  })

  const handleRemove = async (idProductSku) => {
    setLoadingAction({ idProductSku, action: 'remove' })
    try {
      const res = await doRequestPost({
        url: '/product/cart/remove',
        data: { p_id_product_sku: idProductSku }
      })
      doToast({ ...res, toast })
      if (res.response === 'success') setReload(true)
    } catch (e) {
      doToast({ response: 'danger', message: 'Error al quitar.', toast })
    } finally {
      setLoadingAction(null)
    }
  }

  const handleUpdateQty = async (idProductSku, newQty, action) => {
    if (newQty < 1) {
      handleRemove(idProductSku)
      return
    }
    setLoadingAction({ idProductSku, action: action || (newQty > (items?.find(i => i.id_product_sku === idProductSku)?.qty || 0) ? 'plus' : 'minus') })
    try {
      const res = await doRequestPost({
        url: '/product/cart/update-qty',
        data: { p_id_product_sku: idProductSku, p_qty: newQty }
      })
      doToast({ ...res, toast })
      if (res.response === 'success') setReload(true)
    } catch (e) {
      doToast({ response: 'danger', message: 'Error al actualizar.', toast })
    } finally {
      setLoadingAction(null)
    }
  }

  const isRowLoading = (idProductSku) => loadingAction?.idProductSku === idProductSku
  const isAction = (action) => loadingAction?.action === action

  const handlePay = () => {
    if (!items || items.length === 0) {
      doToast({ response: 'warning', message: 'El carrito está vacío.', toast })
      return
    }
    const hasInvalid = items.some(it => !it.stock_ok)
    if (hasInvalid) {
      doToast({ response: 'warning', message: 'Hay ítems sin stock suficiente. Ajusta cantidades o quítalos.', toast })
      return
    }
    setDoSubmit(true)
  }

  const hasItems = items && items.length > 0

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="cart-productos-modal" backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <i className="fa fa-shopping-cart me-2 text-primary" />
          Carrito de productos
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!hasItems && (
          <div className="text-center py-5">
            <i className="fa fa-shopping-basket fa-3x text-muted mb-3 opacity-50" />
            <p className="text-muted mb-0">Tu carrito está vacío.</p>
            <p className="small text-muted mt-1">Agrega productos desde el catálogo.</p>
          </div>
        )}
        {hasItems && (
          <>
            <div className="cart-items-list">
              {items.map(it => (
                <div key={it.id_product_sku} className="cart-item-row d-flex align-items-center flex-wrap">
                  <div className="d-flex align-items-center flex-grow-1 mb-2 mb-md-0">
                    {it.image_product && (
                      <img
                        src={apiResource({ url: it.image_product })}
                        alt=""
                        className="rounded me-3"
                        style={{ width: 56, height: 56, objectFit: 'cover' }}
                      />
                    )}
                    <div>
                      <div className="fw-semibold">{it.name_product}</div>
                      <small className="text-muted">{it.size_product_sku} / {it.color_product_sku}</small>
                      {!it.stock_ok && (
                        <span className="badge bg-warning text-dark ms-2">Sin stock suficiente</span>
                      )}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div className="d-flex align-items-center border rounded cart-qty-controls">
                      <Button
                        size="sm"
                        variant="light"
                        className={`border-0 rounded-start px-2 py-1 cart-qty-btn ${it.qty <= 1 ? 'cart-qty-btn--disabled' : ''}`}
                        disabled={it.qty <= 1 || isRowLoading(it.id_product_sku)}
                        onClick={() => handleUpdateQty(it.id_product_sku, it.qty - 1, 'minus')}
                      >
                        {isRowLoading(it.id_product_sku) && isAction('minus') ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        ) : (
                          '−'
                        )}
                      </Button>
                      <span className="px-3 text-center cart-qty-value">{it.qty}</span>
                      <Button
                        size="sm"
                        variant="light"
                        className={`border-0 rounded-end px-2 py-1 cart-qty-btn ${it.qty >= it.stock_product_sku ? 'cart-qty-btn--disabled' : ''}`}
                        disabled={it.qty >= it.stock_product_sku || isRowLoading(it.id_product_sku)}
                        onClick={() => handleUpdateQty(it.id_product_sku, it.qty + 1, 'plus')}
                      >
                        {isRowLoading(it.id_product_sku) && isAction('plus') ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        ) : (
                          '+'
                        )}
                      </Button>
                    </div>
                    <span className="fw-semibold text-nowrap cart-item-subtotal">
                      MXN {(it.subtotal || 0).toFixed(2)}
                    </span>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      className="text-nowrap cart-btn-remove"
                      disabled={isRowLoading(it.id_product_sku)}
                      onClick={() => handleRemove(it.id_product_sku)}
                    >
                      {isRowLoading(it.id_product_sku) && isAction('remove') ? (
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                      ) : null}
                      Quitar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="cart-total-block d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3 mt-3">
              <div className="w-100 w-sm-auto">
                <div className="d-flex justify-content-between small mb-1">
                  <span>Subtotal</span>
                  <span>MXN {totals.subtotal}</span>
                </div>
                {/* <div className="d-flex justify-content-between small mb-2">
                  <span>Cargo por orden</span>
                  <span>MXN {totals.cargo_por_orden}</span>
                </div> */}
                <div className="d-flex justify-content-between align-items-center fs-5 fw-bold mb-0">
                  <span>Total</span>
                  <span className="text-primary">MXN {totals.total}</span>
                </div>
              </div>
              <Button
                variant="info"
                size="lg"
                className="text-white btn-pay-mp w-100 w-sm-auto"
                onClick={handlePay}
                disabled={doSubmit}
              >
                {doSubmit ? (
                  <span className="d-inline-flex align-items-center">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Procesando…
                  </span>
                ) : (
                  <>
                    <i className="fa fa-credit-card me-2" />
                    Pagar con Mercado Pago
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </Modal.Body>
      {doSubmit && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded-bottom"
          style={{ background: 'rgba(255,255,255,0.92)', zIndex: 1050 }}
        >
          <LoadingIcon />
        </div>
      )}
    </Modal>
  )
}
